import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Alert, AlertTitle, Collapse, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNotificationsContext } from "../../_contexts/notifications-context";
import { parseHtml } from "../../utils/utils";

function AlertService({ showGSG = false, showGlobal = true, divisionCode = null, siteName = null }) {
    const [alertList, setAlertList] = useState([]);
    const [isOpenList, setIsOpenList] = useState([]);
    const { visibleNotifications, dismissNotification } = useNotificationsContext();

    useEffect(() => {
        if (visibleNotifications) {
            let gsgAlerts = [];

            if (showGSG) {
                if (siteName === null) {
                    gsgAlerts = visibleNotifications.gsg?.["ca-sanbernardino"] || [];
                } else {
                    gsgAlerts = visibleNotifications.gsg?.[siteName] || [];
                }
            }
            const globalAlerts = showGlobal ? visibleNotifications?.global || [] : [];
            const divisionAlerts = divisionCode ? visibleNotifications.division?.[divisionCode] || [] : [];

            const combinedAlerts = [
                ...gsgAlerts.map(alert => ({ ...alert, type: "GSG", alertTypeCode: "error" })),
                ...globalAlerts.map(alert => ({ ...alert, type: "CMS" })),
                ...divisionAlerts.map(alert => ({ ...alert, type: "CMS" }))
            ];

            // Sort alerts by alertTypeCode and then by modified date (newest first)
            var typeOrder = ["error", "warning", "info", "success"];
            combinedAlerts.sort((a, b) => {
                const typeA = typeOrder.indexOf(a.alertTypeCode || "info");
                const typeB = typeOrder.indexOf(b.alertTypeCode || "info");

                if (typeA !== typeB) {
                    return typeA - typeB; // Sort by alertTypeCode
                }

                // If alertTypeCode is the same, sort by modified date
                return new Date(b.modifiedDate) - new Date(a.modifiedDate);
            });

            setAlertList(combinedAlerts);
            setIsOpenList(combinedAlerts.map(() => true));
        }
    }, [visibleNotifications]);

    const handleDismiss = (index, alert) => {
        const updatedIsOpenList = [...isOpenList];
        updatedIsOpenList[index] = false;
        setIsOpenList(updatedIsOpenList);
        dismissNotification(alert, alert.type === "GSG");
    };

    const parseAlertContent = alert => {
        if (alert.type === "GSG") {
            try {
                const parsedText = JSON.parse(alert.text);
                return parseHtml(parsedText["en"] || "Message not available");
            } catch (error) {
                console.error("Error parsing GSG alert text:", error);
                return "Invalid message format";
            }
        } else if (alert.type === "CMS") {
            return {
                title: parseHtml(alert.title),
                description: parseHtml(alert.description)
            };
        }
        return { title: "Unknown Alert Type", description: "Message not available" };
    };

    return (
        alertList.length > 0 && (
            <div className="py-4">
                {alertList.map((alert, index) => {
                    const content = parseAlertContent(alert);
                    return (
                        <Collapse key={index} in={isOpenList[index]}>
                            <Alert
                                className="mb-2"
                                severity={alert.alertTypeCode || "info"}
                                sx={{
                                    fontSize: "19px",
                                    "& .MuiAlert-icon": {
                                        fontSize: "32px",
                                        padding: "7px 5px"
                                    }
                                }}
                                action={
                                    alert.alertTypeCode === "error" ? (
                                        <></>
                                    ) : (
                                        <IconButton
                                            aria-label="close"
                                            color="inherit"
                                            onClick={() => handleDismiss(index, alert)}>
                                            <CloseIcon fontSize="inherit" />
                                        </IconButton>
                                    )
                                }>
                                {alert.type === "GSG" ? (
                                    <span>{content}</span>
                                ) : (
                                    <>
                                        <AlertTitle sx={{ fontSize: "23px" }}>{content.title}</AlertTitle>
                                        {content.description}
                                    </>
                                )}
                            </Alert>
                        </Collapse>
                    );
                })}
            </div>
        )
    );
}

AlertService.propTypes = {
    showGSG: PropTypes.bool,
    showGlobal: PropTypes.bool,
    divisionCode: PropTypes.string,
    siteName: PropTypes.string
};

export default AlertService;
